import { IsoDate, Range } from '@softline/core';
import { DateRangePickerShortcut } from '@softline/ui-core/date-input';
import { FiscalYearStart } from '@softline/ui-core/date';
import { PageFilterFactoryRef } from './page-filter.function';
import { createPageFilterItem, PageFilterItemOptions } from './page-filter-item.function';
import { PageFilterItem } from '../types/page-filter-item';

export type PageFilterDateScalarOperator =
  | 'equals' | 'notEquals' | 'before' | 'after' | 'beforeOrEquals' | 'afterOrEquals';

type DateItemBaseOptions<T extends object> = Omit<PageFilterItemOptions<T, IsoDate>, 'operator'>;

export interface PageFilterDateItemOptions<T extends object> extends DateItemBaseOptions<T> {
  /** default 'equals'; in/notIn are not offered - the query mapper rejects them */
  operator?: PageFilterDateScalarOperator;
}

export interface PageFilterDateRangeItemOptions<T extends object> extends DateItemBaseOptions<T> {
  /** extra shortcuts in the pinned dropdown, next to the built-in ones */
  shortcuts?: readonly DateRangePickerShortcut[];
  /** default true */
  showBuiltinShortcuts?: boolean;
  /** the earliest/latest selectable dates */
  totalRange?: Range<IsoDate>;
}

export interface PageFilterDateRangeReferenceItemOptions<T extends object> extends DateItemBaseOptions<T> {
  /** where the fiscal year starts; drives the picker's year and quarter presets */
  fiscalYearStart?: FiscalYearStart;
}

/** A single date on a dateFilter() field; pinned it renders in the generic dropdown. */
export function pageFilterDateItem<T extends object>(
  ref: PageFilterFactoryRef,
  options: PageFilterDateItemOptions<T>
): PageFilterItem<T, IsoDate> {
  const item = createDateItem(ref, { ...options, operator: options.operator ?? 'equals' }, 'pageFilterDateItem');
  item.kind = 'date';
  ref.items.push(item);
  return item;
}

/** A from/to period (operator between); pinned it renders the design-system date range dropdown. */
export function pageFilterDateRangeItem<T extends object>(
  ref: PageFilterFactoryRef,
  options: PageFilterDateRangeItemOptions<T>
): PageFilterItem<T, IsoDate> {
  const { shortcuts, showBuiltinShortcuts, totalRange, ...rest } = options;
  const item = createDateItem(ref, { ...rest, operator: 'between' }, 'pageFilterDateRangeItem');
  item.kind = 'dateRange';
  item.date = { shortcuts, showBuiltinShortcuts, totalRange };
  ref.items.push(item);
  return item;
}

/**
 * A period plus a comparison period. The node stays one between filter carrying `reference`;
 * the query mapper expands it to an OR of two dateInRange filters. Pinned it renders the
 * design-system comparison picker.
 */
export function pageFilterDateRangeReferenceItem<T extends object>(
  ref: PageFilterFactoryRef,
  options: PageFilterDateRangeReferenceItemOptions<T>
): PageFilterItem<T, IsoDate> {
  const { fiscalYearStart, ...rest } = options;
  const item = createDateItem(ref, { ...rest, operator: 'between' }, 'pageFilterDateRangeReferenceItem');
  item.kind = 'dateRangeReference';
  item.date = { fiscalYearStart };
  ref.items.push(item);
  return item;
}

function createDateItem<T extends object>(
  ref: PageFilterFactoryRef,
  options: PageFilterItemOptions<T, IsoDate>,
  context: string
): PageFilterItem<T, IsoDate> {
  const item = createPageFilterItem<T, IsoDate>(ref, options, context);
  // dateTime/dateTimeTz mappers have no reference branch yet, and the pinned controls format IsoDate
  if (item.config.type !== 'date')
    throw new Error(`[${context}] Field ${String(options.field)} is not a date filter`);
  return item;
}
